// @flow
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Build as DraftIcon, HourglassEmpty as ValidatingIcon, Work as ProcessingIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import routes from '../routes';
import Layout from '../component/Layout';
import ProcessAgreementData from '../component/ProcessAgreementData';
import ProcessCompanyData from '../component/ProcessCompanyData';
import ProcessDocumentData from '../component/ProcessDocumentData';
import ProcessPaymentData from '../component/ProcessPaymentData';
import ProcessPersonalData from '../component/ProcessPersonalData';
import ProcessVideoData from '../component/ProcessVideoData';
import { AuthContext } from '../context/auth';
import SignProcess, { ProcessResponse } from '../model/SignProcess';
import SignProcessCompany from '../model/SignProcessCompany';
import { toSentence } from '../util/string';
import ProcessBillingData from '../component/ProcessBillingData';
import User from '../model/User';
import type { AgreementProps, PaymentProps } from '../model/props';
import ApiResponse from '../model/ApiResponse';
import { doPostMultipart } from '../util/api';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900']
  },
  validating: {
    color: theme.palette.grey['700']
  },
  processing: {
    color: theme.palette.grey['900']
  },
  grey: {
    color: theme.palette.text.secondary
  },
  error: {
    color: theme.palette.error.main
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  bottomButton: {
    marginTop: theme.spacing(2)
  }
}));

type Props = {
  match?: any,
};

const personalData = 'personal-data';
const companyData = 'company-data';
const documentsData = 'document-data';
const billingData = 'billing-data';
const paymentData = 'payment-data';
const agreementData = 'agreement-data';
const videoData = 'video-data';

const getExpandedKey = (process, user) => {
  if (!!process) {
    if (!process.hasDataLoaded()) {
      return personalData;
    } else if (process.haveCompanyData && !process.completedCompanyData) {
      return companyData;
    } else if (/*process.internalValidation && */process.isDataLoaded() && !process.hasDocuments()) {
      return documentsData;
    } else if (process.hasDocuments() && !!user && !user.isRetail && !process.hasBilling()) {
      return billingData;
    } else if ((process.hasBilling() || (!!user && user.isRetail)) && !process.hasPaymentLoaded()) {
      return paymentData;
    } else if (process.hasPaymentLoaded() && !process.hasAgreementLoaded()) {
      return agreementData;
    } else if (!process.hasVideoLoaded()) {
      return videoData;
    }
    return null;
  }
};

const Process = (props: Props) => {
  const { doGet, doPost, doDelete, setUser, user } = useContext(AuthContext);
  const { t } = useTranslation();
  const { match } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [process, setProcess] = useState <? SignProcess > ();
  const [expanded, setExpanded] = useState <? string > ();
  const [processing, setProcessing] = useState < boolean > (false);

  useEffect(() => {
    const expandedKey = getExpandedKey(process, user);
    setExpanded(expandedKey);
  }, [process, user]);

  const loadProcess = () => {
    if (!process && match && match.params && match.params.code) {
      doGet(`/customer/sign-process/${match.params.code}`)
        .then((response: ApiResponse) => {
          let theProcess = SignProcess.parse(response.data);
          setProcess(theProcess);
          const expandedKey = getExpandedKey(theProcess, user);
          setExpanded(expandedKey);
        });
    }
  };

  const handleExpanded = (key: string) => (event, isExpanded: boolean) => {
    setExpanded(isExpanded ? key : null);
  };

  useEffect(loadProcess, []);

  const saveProcessData = (processData: SignProcess) => {
    if (!!process) {
      setProcessing(true);
      doPost(`/customer/sign-process/${process.code}`, processData.toDataMap())
        .then((response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Data saved'), {
              variant: 'success'
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error'
            });
          }
          setProcessing(false);
        });
    }
  };

  const saveCompanyData = (companyData: SignProcessCompany) => {
    if (!!process) {
      setProcessing(true);
      doPost(`/customer/sign-process/${process.code}/company`, companyData.toDataMap())
        .then((response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Data saved'), {
              variant: 'success'
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error'
            });
          }
          setProcessing(false);
        });
    }
  };

  const saveDocumentData = ({ fileType, file }) => {
    if (!!process) {
      setProcessing(true);
      const data = {
        name: file.name,
        type: fileType
      };
      const files = {
        file: file
      };
      doPostMultipart(`/customer/sign-process/${process.code}/document`, data, files)
        .then((response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Document saved'), {
              variant: 'success'
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error'
            });
          }
          setProcessing(false);
        });
    }
  };

  const deleteDocumentData = ({ fileType }) => {
    if (!!process) {
      setProcessing(true);
      doDelete(`/customer/sign-process/${process.code}/document/${fileType}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Document removed'), {
              variant: 'success'
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error'
            });
          }
          setProcessing(false);
        });
    }
  };

  const saveBillingData = (data: PaymentProps): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise < boolean > (resolve => {
        doPost(`/customer/sign-process/${process.code}/billing-data`, data)
          .then((response: ApiResponse) => {
            if (response.ok && !!response.data) {
              let theProcess = SignProcess.parse(response.data.process);
              setProcess(theProcess);
              const user = User.parse(!!response.data ? response.data.user : {});
              if (!!user) {
                setUser(user);
                const expandedKey = getExpandedKey(theProcess, user);
                setExpanded(expandedKey);
              }
              enqueueSnackbar(t('Billing data saved'), {
                variant: 'success'
              });
            } else if (!response.is401Error()) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise < boolean > (resolve => resolve(false));
  };

  const savePaymentData = (payment: PaymentProps): Promise<boolean> => {
    if (!!process) {
      const { file, mode, coupon } = payment;
      setProcessing(true);
      let data = {
        mode,
        coupon,
      };
      let files = null;
      let path = 'payment-way';
      if (!!file) {
        data = {
          mode,
          name: file.name,
          coupon,
        };
        files = { file };
        path = 'proof-of-payment';
      }
      return new Promise < boolean > (resolve => {
        doPost(`/customer/sign-process/${process.code}/${path}`, data, files)
          .then((response: ApiResponse) => {
            if (response.ok) {
              let theProcess = SignProcess.parse(response.data);
              setProcess(theProcess);
              const expandedKey = getExpandedKey(theProcess, user);
              setExpanded(expandedKey);
              enqueueSnackbar(t('Payment saved'), {
                variant: 'success'
              });
            } else if (!response.is401Error()) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise < boolean > (resolve => resolve(false));
  };

  const applyCoupon = (coupon: string): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise < boolean > (resolve => {
        process.applyCoupon(coupon)
          .then((response: ApiResponse) => {
            if (response.ok) {
              let theProcess = SignProcess.parse(response.data);
              setProcess(theProcess);
              const expandedKey = getExpandedKey(theProcess, user);
              setExpanded(expandedKey);
              enqueueSnackbar(t('Applied coupon'), {
                variant: 'success'
              });
            } else if (!response.is401Error()) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise < boolean > (resolve => resolve(false));
  };

  const saveAgreementData = (data: AgreementProps): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise(resolve => {
        process.saveAgreement(data)
          .then((response: ProcessResponse) => {
            if (response.ok) {
              setProcess(response.process);
              const expandedKey = getExpandedKey(response.process, user);
              setExpanded(expandedKey);
              enqueueSnackbar(t('Contract agreement saved'), {
                variant: 'success'
              });
            } else if (!!response.message) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise < boolean > (resolve => resolve(false));
  };

  const saveVideoData = ({ file }): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise < boolean > (resolve => {
        process.saveVideo(file)
          .then((response: ApiResponse) => {
            if (response.ok) {
              let theProcess = SignProcess.parse(response.data);
              setProcess(theProcess);
              const expandedKey = getExpandedKey(theProcess, user);
              setExpanded(expandedKey);
              enqueueSnackbar(t('Verification saved'), {
                variant: 'success'
              });
              enqueueSnackbar(t('Your process is VIDEO_LOADED'), {
                variant: 'success'
              });
              setExpanded(null);
            } else if (!response.is401Error()) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise < boolean > (resolve => resolve(false));
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home} />;
  }

  return (
    <Layout>
      {!process && [1, 2, 3, 4, 5].map((index) => (
        <Skeleton key={index} />
      ))}
      {!!process && (
        <>
          <Typography variant='h5'>
            {process.isDraft()
              && <DraftIcon className={classes.draft} />}
            {(process.hasVideoLoaded() && !process.isProcessing() && !process.isCompleted())
              && <ValidatingIcon className={classes.draft} />}
            {(process.isProcessing() || process.isCompleted())
              && <ProcessingIcon className={classes.processing} />}
            &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
          </Typography>
          <Typography variant='subtitle2' className={classes.grey}>
            {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()}.
          </Typography>
          {!!process.containerTitle && <Typography variant='subtitle2' className={classes.grey}>
            {t('Container')}: {process.containerTitle} ${process.containerPrice.toFixed(2)}.
          </Typography>}
          <Typography variant='subtitle2' className={classes.grey}>
            {t('Total price')}: ${process.price.toFixed(2)}.
          </Typography>
          <small>&nbsp;</small>
          {!!process && <ProcessPersonalData
            code={personalData}
            disabled={processing || process.hasDocumentsValidated()}
            onSave={saveProcessData}
            expanded={expanded === personalData}
            onExpanded={(key) => handleExpanded(key)}
            process={process} />}
          {(!!process && process.haveCompanyData) && <ProcessCompanyData
            code={companyData}
            disabled={processing || process.hasDocumentsValidated()}
            onSave={saveCompanyData}
            expanded={expanded === companyData}
            onExpanded={(key) => handleExpanded(key)}
            parent={process}
            process={process.companyData ?? new SignProcessCompany()} />}
          {(!!process && !!process.hasDataLoaded()) && <ProcessDocumentData
            code={documentsData}
            loading={processing}
            disabled={processing || process.hasDocumentsValidated()}
            onSave={saveDocumentData}
            onDelete={deleteDocumentData}
            expanded={expanded === documentsData}
            onExpanded={(key) => handleExpanded(key)}
            process={process} />}
          {(!!process && !!process.hasDocuments() && !!user && !user.isRetail) && <ProcessBillingData
            code={billingData}
            disabled={processing || process.hasDocumentsValidated()}
            onSave={saveBillingData}
            expanded={expanded === billingData}
            onExpanded={(key) => handleExpanded(key)}
            process={process} />}
          {(!!process && !!process.hasDocuments() && (process.hasBilling() || (!!user && user.isRetail))) && <ProcessPaymentData
            code={paymentData}
            loading={processing}
            disabled={processing || process.hasDocumentsValidated()}
            onSave={savePaymentData}
            onApplyCoupon={applyCoupon}
            expanded={expanded === paymentData}
            onExpanded={(key) => handleExpanded(key)}
            process={process} />}
          {(!!process && !!process.hasDocuments() && !!process.hasPaymentLoaded()) && <ProcessAgreementData
            isRetailClient={false}
            code={agreementData}
            disabled={processing || process.hasDocumentsValidated()}
            processing={processing}
            onSave={saveAgreementData}
            expanded={expanded === agreementData}
            onExpanded={(key) => handleExpanded(key)}
            contractAgreementUrl={process.contractAgreementUrl}
            templateAgreementUrl={process.templateAgreementUrl}
            process={process} />}
          {(!!process && !!process.hasAgreementLoaded()) && <ProcessVideoData
            code={videoData}
            disabled={processing || process.hasDocumentsValidated()}
            processing={processing}
            onSave={saveVideoData}
            expanded={expanded === videoData}
            onExpanded={(key) => handleExpanded(key)}
            process={process} />}
        </>
      )}
    </Layout>
  );
};

export default Process;
